import { array, option } from 'fp-ts';
import { constNull, pipe } from 'fp-ts/function';
import { TextKey } from '@mews-commander/enums';
import { OpenPayout } from '@mews-commander/data-access';
import { StandaloneTable as Table, useTableState } from '@optimus-web/core';
import { useGetOpenPayoutSummary, useGetOpenPayoutTransactions } from '../queries';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '../constants';
import { useGetBreadcrumbs } from '../hooks';
import { getPayoutDetailColumns } from './tableMapping';
import { PayoutReportLayout } from './PayoutReportLayout';
import { OpenPayoutSummaryCard } from './OpenPayoutSummaryCard';
import { PayoutSummaryGrid } from './PayoutSummaryGrid';

export const OpenBalanceDetail = ({ currencyCode, guideLink }: { currencyCode: string; guideLink: string }) => {
    const [tableState, setTableState] = useTableState<OpenPayout>({
        pagination: {
            pageSize: DEFAULT_PAGE_SIZE,
            pageIndex: DEFAULT_PAGE_INDEX,
        },
    });

    const {
        data: { openPayouts: openPayoutSummary } = {},
        isFetching: isOpenPayoutSummaryDataFetching,
        isError: hasOpenPayoutSummaryDataFailed,
    } = useGetOpenPayoutSummary();

    const {
        data: openPayoutTransactions = { data: [], totalRowCount: 0 },
        isFetching: isOpenPayoutTransactionsDataFetching,
        isError: hasOpenPayoutTransactionsDataFailed,
    } = useGetOpenPayoutTransactions({ ...tableState, currencyCode });

    const breadcrumbs = useGetBreadcrumbs({ headerTextKey: TextKey.PayoutOpenBalanceDetail });

    const openPayoutSummaryCard = pipe(
        option.fromNullable(openPayoutSummary),
        option.flatMap(array.findFirst(({ net }) => net.currencyCode === currencyCode)),
        option.getOrElseW(constNull)
    );

    return (
        <PayoutReportLayout
            breadcrumbs={breadcrumbs}
            hasFailed={hasOpenPayoutSummaryDataFailed || hasOpenPayoutTransactionsDataFailed}
            isFetching={isOpenPayoutSummaryDataFetching}
            guideLink={guideLink}
            summary={
                openPayoutSummaryCard && (
                    <PayoutSummaryGrid>
                        <OpenPayoutSummaryCard openPayoutSummary={openPayoutSummaryCard} />
                    </PayoutSummaryGrid>
                )
            }
        >
            <Table
                data={openPayoutTransactions.data}
                uniqueRowId="id"
                loading={isOpenPayoutTransactionsDataFetching}
                columns={getPayoutDetailColumns({})}
                amountOfRows={openPayoutTransactions.totalRowCount}
                state={tableState}
                onStateChange={setTableState}
            />
        </PayoutReportLayout>
    );
};
